// BUILD-046 tasks 8.19 / 8.24 / 8.25: the small pieces the My Work host adds to a My tasks row or card, kept here so the host page changes stay
// small and traceable. The rules are in the tested models; these components only draw them.
//
//   DoneNotices : the inline Undo offered right after a Done, kept above the list because the completed row leaves the active tab (Req 8.12)
//
// Correction cycle 1bb88b3e (stage B2a, item N9): Done is THE emphasised action of a Mine row - a solid green button with its word on it. The owner-left
// indicator and the Reassign… / Return to queue controls of the owner's own row moved into MineRow (the row's one "More actions" menu).
import { itemDone } from './doneModel';
import type { DoneState } from './doneModel';
// D1 display cleanup: the task title in a notice is shown without the "(FICTIONAL)" test marker.
import { stripTestMarker } from './displayNames';

// Layout-4: the Done button itself is DoneSplit.tsx (the joined Done + "Done with note" caret); this file keeps the Undo notices.

export interface DoneNoticesProps {
  done: DoneState;
  titleOf: (id: string) => string;
  onUndo: (id: string) => void;
  onDismiss: (id: string) => void;
  /**
   * For a task completed with "Done with note": the plain outcome ("Completed. Note saved.") to say in this SAME line instead of "was marked done", so one
   * completion is one message with its Undo. Null (or absent) keeps the ordinary wording.
   */
  messageOf?: (id: string) => string | null;
}

export function DoneNotices({ done, titleOf: storedTitleOf, onUndo, onDismiss, messageOf }: DoneNoticesProps) {
  // The title is SHOWN (and named for assistive technology) without the "(FICTIONAL)" test marker; the stored title is the caller's and is not changed.
  const titleOf = (id: string) => {
    const shown = stripTestMarker(storedTitleOf(id));
    return shown !== '' ? shown : 'The task';
  };
  const ids = Object.keys(done.items).filter((id) => itemDone(done, id).undo !== 'none');
  if (ids.length === 0) return null;
  return (
    <ul className="mb-3 space-y-1" aria-label="Recently completed">
      {ids.map((id) => {
        const state = itemDone(done, id);
        return (
          <li key={id} role="status" aria-live="polite" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm bg-green-50 text-green-900 border border-green-200 rounded px-3 py-2">
            <span>{(() => { const message = messageOf?.(id) ?? null; return message === null ? `${titleOf(id)} was marked done.` : `${titleOf(id)}: ${message}`; })()}</span>
            <button type="button" disabled={state.undo === 'saving'} onClick={() => onUndo(id)} aria-label={`Undo Done: ${titleOf(id)}`} className="underline font-medium disabled:opacity-50">
              {state.undo === 'saving' ? 'Undoing…' : 'Undo'}
            </button>
            {state.undoFailure !== null && (
              <span role="alert" className="text-red-700">
                {state.undoFailure.headline}
                {state.undoFailure.detail !== null && <span className="ml-1">{state.undoFailure.detail}</span>}
              </span>
            )}
            <span className="flex-1" />
            <button type="button" disabled={state.undo === 'saving'} onClick={() => onDismiss(id)} aria-label="Dismiss message" className="font-bold">
              &times;
            </button>
          </li>
        );
      })}
    </ul>
  );
}
